import {ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import 'firebase/firestore';
import {BehaviorSubject, interval, Subject, Subscription} from 'rxjs';
import {Observable} from 'rxjs/internal/Observable';
import {combineLatest, first, map, take, takeUntil} from 'rxjs/operators';
import {Job, JobProduct, LinkModel, MemberProduct, Response} from '../../models';
import {
    AgencyService,
    AssetsService,
    AuthenticationService,
    DialogService,
    JobsService,
    ProductsService,
    SnackbarService
} from '../../services';
import {UploadService} from '../../services/upload.service';
import {AddEditInvoiceComponent} from '../add-edit-invoice/add-edit-invoice.component';
import {AddJobProductComponent} from '../add-job-product/add-job-product.component';
import {DownloadSelectionModalComponent} from '../download-selection-modal/download-selection-modal.component';
import {NotifyAgentDialogComponent} from '../notify-agent-dialog/notify-agent-dialog.component';
import {FilterDownloadSelectionModalComponent} from '../filter-download-selection-modal/filter-download-selection-modal.component';
import {collection, doc, getFirestore, onSnapshot} from 'firebase/firestore';
import includes from 'lodash/includes';
import {AddLinkDialogComponent} from './add-link-dialog/add-link-dialog.component';
import orderBy = require('lodash/orderBy');

@Component({
    selector: 'prism-job-album',
    templateUrl: './job-album.component.html',
    styleUrls: ['./job-album.component.scss']
})

export class JobAlbumComponent implements OnInit, OnDestroy {
    memberProductsLoading$ = this.productsService.areMemberProductsLoading$;
    isLuxEngine$ = this.authService.isLuxEngine$;
    jobProductsLoading$ = this.productsService.areJobProductsLoading$;
    jobProducts$: Observable<JobProduct[]> = this.productsService.jobProducts$;
    albumProducts$: Observable<any[]> = this.productsService.albumProducts$;
    selectedAssetsCount$: Observable<any> = this.assetsService.selectedAssetsCount$;
    private appDataSub: () => void;
    private pollingSubscription: Subscription;
    private readonly POLLING_INTERVAL = 30000; // 30 seconds
    job: Job;
    jobLoading: boolean;
    productToAdd: BehaviorSubject<MemberProduct> = new BehaviorSubject<MemberProduct>(null);
    dialogRef: MatDialogRef<any>;
    subDestroyer$: Subject<void> = new Subject<void>();
    assetsData: any = {};
    assetPaths: string[] = [];
    agencyData: any;
    dropdownProductSelected = false;
    fetchingAssetPaths = false;
    jobDownloadingNotification: Subject<void> = new Subject<void>();
    memberProducts: MemberProduct[] = [];
    hasActiveProducts: boolean = true;
    jobInvoiced: boolean = true;
    placeholderText: string;
    loading;
    links: LinkModel[] = [];
    scrollContainer: any;

    constructor(
        private jobsService: JobsService,
        private router: Router,
        private agencyService: AgencyService,
        private productsService: ProductsService,
        private snackbarService: SnackbarService,
        private dialogService: DialogService,
        private assetsService: AssetsService,
        private authService: AuthenticationService,
        private dialog: MatDialog,
        private uploadService: UploadService,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        this.productsService.memberProducts$
            .pipe(
                takeUntil(this.subDestroyer$),
                map(memberProducts => {
                    return orderBy(memberProducts, ['ProductName'], 'asc');
                })
            )
            .subscribe((memberProducts) => {
                this.memberProducts = memberProducts;
                this.hasActiveProducts = memberProducts.filter(product => product.active).length >= 1;
            });


        this.loading = this.productsService.areMemberProductsLoading$
            .pipe(
                takeUntil(this.subDestroyer$),
                combineLatest(this.productsService.areJobProductsLoading$)
            )
            .subscribe((loading) => {
                this.placeholderText = includes(loading, true) ? `Loading. Please wait...` : 'Select a product to add';
            });

        // Subscribe to upload completion to refresh view when new images are uploaded
        this.uploadService.uploadStats$
            .pipe(
                takeUntil(this.subDestroyer$)
            )
            .subscribe((stats) => {
                // When uploads complete, refresh the job products to show new images
                if (stats.total > 0 && stats.completed === stats.total) {
                    console.log('[JobAlbum] All uploads completed, refreshing album page');
                    // Small delay to ensure backend has updated Firestore
                    setTimeout(() => {
                        if (this.job && this.job.id) {
                            this.ngZone.run(() => {
                                this.productsService.getJobProducts(this.job.id.toString())
                                    .pipe()
                                    .subscribe(
                                        () => {
                                            this.productsService.refreshAlbumPage();
                                            this.cdr.detectChanges();
                                            console.log('[JobAlbum] Album refreshed after upload completion');
                                        },
                                        (err) => console.error('[JobAlbum] Error refreshing after upload:', err)
                                    );
                            });
                        }
                    }, 1500);
                }
            });

        this.jobLoading = false;
        if (!this.jobsService.selectedJob) {
            let memberData = this.authService.memberCurrentData;
            let jobId = this.router.url.split('/')[3];
            this.jobLoading = true;
            this.jobsService.getJob(jobId, memberData.mainMemberUid)
                .pipe(take(1))
                .subscribe((job) => {
                    this.jobsService.setSelectedJob(job);
                    this.jobInvoiced = this.isJobInvoiced(job);
                    this.job = job;
                    this.links = job.links;
                    this.jobLoading = false;
                    this.setup(jobId);
                }, error => {
                    console.error('Error fetching job and links:', error);
                    this.jobLoading = false;
                });

        } else {
            this.job = this.jobsService.selectedJob;
            this.links = this.jobsService.selectedJob.links;
            this.setup();
            this.jobInvoiced = this.isJobInvoiced(this.job);
        }
        this.scrollContainer = document.querySelector('.scroll-container');
        
        // Start polling for new assets
        this.startPolling();
    }

    setup(jobId = null) {
        const {memberProfileUid, id, agency} = this.job;
        const firestoreRef = getFirestore();
        let docRef = doc(collection(firestoreRef, 'assetCounters'), `${memberProfileUid}_${id}`);

        this.appDataSub = onSnapshot(docRef,
            snapshot => {
                const snapshotData = snapshot.data();
                console.log('[JobAlbum] Firestore snapshot received:', {
                    exists: snapshot.exists(),
                    hasData: !!snapshotData,
                    docId: snapshot.id,
                    data: snapshotData
                });
                
                // Run inside Angular zone to trigger change detection
                this.ngZone.run(() => {
                    // Create a new object reference to ensure Angular detects the change
                    this.assetsData = {...(snapshotData || {})};
                    
                    // Manually trigger change detection
                    this.cdr.detectChanges();
                    
                    console.log('[JobAlbum] Assets data updated, change detection triggered');
                });
            },
            (error) => {
                console.error('[JobAlbum] Firestore snapshot error:', error);
            }
        );

        this.productsService.clearAllProducts();

        if (!!agency) {
            this.agencyService.getAgency(agency)
                .toPromise()
                .then(agency => {
                    this.agencyData = agency;
                })
                .catch(e => {
                    console.error('Error fetching agency data', e);
                    this.snackbarService.handleError('Error fetching agency data');
                });
        }
        this.productsService.getJobProducts(jobId)
            .pipe(
                takeUntil(this.subDestroyer$)
            )
            .subscribe(
                () => {
                    this.productsService.refreshAlbumPage();
                },
                (err) => console.error(err)
            );
        this.productsService.getMemberProducts({getAll: true})
            .pipe(
                takeUntil(this.subDestroyer$),
                map(memberProductsResponse => {
                    const memberProducts = memberProductsResponse.data || [];
                    memberProductsResponse.data = orderBy(memberProducts, ['productName'], 'asc');
                    return memberProductsResponse;
                })
            )
            .subscribe();
    }

    openShareModal() {
        this.dialogService.openDialog(NotifyAgentDialogComponent, {job: this.job}, {width: '600px', autoFocus: false});
    }

    navToJob() {
        const parsedUrl = this.router.routerState.snapshot.url.split('/');
        parsedUrl.pop();
        this.router.navigate(parsedUrl);
    }

    openAddProductDialog() {
        this.productsService.switchPostJobProductLoaderOn();
        const product = this.productToAdd.value;
        this.productsService.setProductToAdd(product);
        this.dialogRef = this.dialogService.openDialog(AddJobProductComponent, this.productToAdd.value, {
            panelClass: 'add-job-product-dialog'
        });
    }

    setSelectedProduct(product) {
        this.dropdownProductSelected = true;
        this.productToAdd.next(product);
    }

    navToMasterProducts() {
        this.router.navigate(['/home', 'products']);
    }

    downloadAllAssets() {
        this.fetchingAssetPaths = true;
        this.assetsService.getAssetsBucketPaths(this.job.id)
            .pipe(
                first(d => !!d)
            )
            .subscribe(
                (assetsResponse: Response) => {
                    this.assetPaths = assetsResponse.data;
                    let assets = [];
                    Object.values(this.assetsData).forEach((item) => {
                        Object.entries(item).forEach(([key, val]) => {
                            if (!key.endsWith('_watermarkFileCount') && !key.endsWith('_webFileCount')) {
                                assets = assets.concat(val);
                            }
                        });
                    });
                    this.fetchingAssetPaths = false;
                    this.dialogService.openDialog(FilterDownloadSelectionModalComponent, {
                        allAssetPaths: this.assetPaths,
                        assets: assets,
                        downloadPrefix: `job_${this.job.id}`
                    }, {
                        panelClass: 'filter-download-selection-dialog'
                    });
                },
                err => {
                    this.fetchingAssetPaths = false;
                    console.error('Error getting asset paths:', err);
                    this.snackbarService.handleError('Problem while collecting file paths. You will not be able to download your jobs files.');
                }
            );
    }

    downloadSelectedAssets() {
        this.fetchingAssetPaths = true;
        this.assetsService.getAssetsBucketPaths(this.job.id)
            .pipe(
                first(d => !!d)
            )
            .subscribe(
                (assetsResponse: Response) => {
                    let selectedAssets = this.assetsService.getSelectedAssets();
                    const filePathNames = selectedAssets.map((asset) => asset.finalPath);
                    this.assetPaths = assetsResponse.data;
                    let filteredAssets = [];

                    for (let filePath of filePathNames) {
                        const [mUid, jUid, pUid, serviceUid, finals, filenameTail] = filePath.split('/');
                        this.assetPaths.forEach((item) => {
                            if ((item.startsWith(`${mUid}/${jUid}/${pUid}/${serviceUid}`) && (item.endsWith(`/finals/${filenameTail}`) || item.endsWith(`/finals/web_${filenameTail}`) || item.endsWith(`/finals/watermark_${filenameTail}`)))) {
                                filteredAssets.push(item);
                            }
                        });
                    }
                    this.fetchingAssetPaths = false;
                    this.notifyJobDownload();
                    this.dialogService.openDialog(DownloadSelectionModalComponent, {allAssetPaths: filteredAssets}, {
                        panelClass: 'download-selection-dialog'
                    });
                },
                err => {
                    this.fetchingAssetPaths = false;
                    console.error('Error getting asset paths:', err);
                    this.snackbarService.handleError('Problem while collecting file paths. You will not be able to download your jobs files.');
                }
            );
    }

    isJobInvoiced(job) {
        return (
            ((!!job.invoices && job.invoices.length > 0) && (!job.productsToInvoice)) ||
            ((!!job.unsyncedInvoices && job.unsyncedInvoices.length > 0) && (!job.productsToInvoice)) ||
            this.authService.memberCurrentData.isContractor ||
            ((job.charged === false) && !job.productsToInvoice));
    }

    openInvoiceDialog(job) {
        this.jobsService.setSelectedJob(job);
        const dialogRef = this.dialogService.openDialog(AddEditInvoiceComponent, {
            job,
            memberProfileUid: this.authService.getUserUid()
        }, {width: '90vw'});

        dialogRef.afterClosed()
            .pipe(take(1))
            .subscribe((result: 'charged' | 'not_charged' | 'canceled') => {
                if (result === 'charged') {
                    this.jobsService.setSelectedJobInvoicesPlaceholder();
                    this.job = this.jobsService.selectedJob;
                    this.jobInvoiced = true;
                } else if (result === 'not_charged') {
                    this.jobsService.setSelectedJobChargedState(false);
                    this.job = this.jobsService.selectedJob;
                    this.jobInvoiced = true;
                } else {
                    this.jobInvoiced = false;
                }
            });
    }

    openAddLinkDialog(): void {
        const dialogRef = this.dialog.open(AddLinkDialogComponent, {
            data: {
                jobId: this.getJobIdFromRoute()
            }
        });

        dialogRef.afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.snackbarService.showSnackbar('Link saved successfully!');
                    this.refreshLinks();
                }
            });
    }

    refreshLinks(): void {
        this.jobsService.getJob(this.getJobIdFromRoute())
            .pipe(take(1))
            .subscribe(
                (job) => {
                    this.ngZone.run(() => {
                        this.jobsService.setSelectedJob(job);
                        this.job = job;
                        this.links = job.links || [];
                        this.cdr.detectChanges();
                    });
                },
                (error) => {
                    console.error('Error reloading job:', error);
                }
            );
    }


    scrollLeft() {
        this.scrollContainer.scrollLeft -= 100;
    }

    scrollRight() {
        this.scrollContainer.scrollLeft += 100;
    }

    notifyJobDownload() {
        this.jobDownloadingNotification.next();
    }

    getJobIdFromRoute(): number {
        return Number(this.router.url.split('/')[3]);
    }

    /**
     * Start polling to periodically check for new assets
     */
    private startPolling(): void {
        console.log('[JobAlbum] Starting polling mechanism, interval:', this.POLLING_INTERVAL / 1000, 'seconds');
        
        this.pollingSubscription = interval(this.POLLING_INTERVAL)
            .pipe(takeUntil(this.subDestroyer$))
            .subscribe(() => {
                if (this.job && this.job.id) {
                    console.log('[JobAlbum] Polling: Refreshing job products');
                    this.refreshJobProducts();
                }
            });
    }

    /**
     * Refresh job products without showing loading indicators
     */
    private refreshJobProducts(): void {
        this.productsService.getJobProducts(this.job.id, false)
            .pipe(take(1))
            .subscribe(
                () => {
                    this.ngZone.run(() => {
                        this.productsService.refreshAlbumPage();
                        this.cdr.detectChanges();
                        console.log('[JobAlbum] Polling: Job products refreshed');
                    });
                },
                (err) => console.error('[JobAlbum] Polling: Error refreshing job products:', err)
            );
    }

    ngOnDestroy() {
        console.log('[JobAlbum] Component destroying, cleaning up subscriptions');
        
        // Stop polling
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
        }
        
        this.assetsService.jobAlbumOnDestroy(this.job?.id);
        this.subDestroyer$.next();
        this.appDataSub();
    }
}
